import type { GroupChannel } from '@sendbird/chat/groupChannel';
import React from 'react';
import {
    BodyMediumEmphasized,
    ExternalLinkIcon,
    H6Emphasized,
    XStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import type { Member } from '../../entities/entityTypes';
import { getSoloOrJointMemberName } from '../../entities/membership/membershipUtils';
import { useMembershipOfSendbirdChannel } from '../hooks/useMembershipOfSendbirdChannel';

interface ChannelHeaderProps {
    currentChannel: GroupChannel | null;
    textSize: 'small' | 'large';
    shouldShowLinkToMembershipDetails?: boolean;
}

export const ChannelHeader = ({
    currentChannel,
    textSize = 'large',
    shouldShowLinkToMembershipDetails = false,
}: ChannelHeaderProps) => {
    const membership = useMembershipOfSendbirdChannel(currentChannel);
    const memberName = getSoloOrJointMemberName(
        membership?.users as Array<Partial<Member> | undefined> | undefined,
    );
    const channelHeaderText =
        memberName == null ? '' : `Messaging with ${memberName}`;

    return (
        <XStack
            marginHorizontal={16} // would use padding instead, but we don't want the borders to be full width
            width="calc(100% - 32px)" // full width, minus the marginHorizontal value
            paddingTop={textSize === 'large' ? 0 : 12}
            paddingLeft={4}
            paddingBottom={12}
            borderBottomWidth={1}
            borderBottomColor="$gray300"
            justifyContent="space-between"
            alignItems="center"
        >
            {textSize === 'small' && (
                <BodyMediumEmphasized>{channelHeaderText}</BodyMediumEmphasized>
            )}
            {textSize === 'large' && (
                <H6Emphasized>{channelHeaderText}</H6Emphasized>
            )}
            {shouldShowLinkToMembershipDetails && membership != null && (
                <ViewMembershipButton membershipId={membership.id} />
            )}
        </XStack>
    );
};

const ViewMembershipButton = ({ membershipId }: { membershipId: string }) => {
    return (
        <XStack alignItems="center" gap={2}>
            <Button
                variant="secondary"
                size="small"
                href={{
                    pathname: '/(app)/membership/[id]',
                    params: { id: membershipId },
                }}
            >
                View Membership
            </Button>
            <Button
                variant="tertiary"
                size="small"
                href={{
                    pathname: '/(app)/membership/[id]',
                    params: { id: membershipId },
                }}
                target="_blank"
                icon={ExternalLinkIcon}
            />
        </XStack>
    );
};
